/**
 * AI Assistant Page
 * Chat interface for AI-powered property assistance
 */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Icon from "../../components/AppIcon";
import AdminLayout from "../../components/layouts/AdminLayout";
import UserLayout from "../../components/layouts/UserLayout";
import ProtectedRoute from "../../components/navigation/ProtectedRoute";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import {
	useChatMutation,
	useGetConversationQuery,
	useGetConversationsQuery,
	type AIMessage,
} from "../../store/api/aiChatApi";
import { useGetProfileQuery } from "../../store/api/authApi";
import {
	useListPropertiesQuery,
	type Property,
} from "../../store/api/propertiesApi";

const AIAssistant: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const propertyId = searchParams.get("property_id");
	const conversationIdParam = searchParams.get("conversation_id");

	const { data: user } = useGetProfileQuery();
	const { data: propertiesData } = useListPropertiesQuery({ page_size: 100 });
	const { data: conversationsData } = useGetConversationsQuery();
	const { data: conversationData, refetch: refetchConversation } =
		useGetConversationQuery(conversationIdParam || "", {
			skip: !conversationIdParam,
		});

	const [chat, { isLoading: isSending }] = useChatMutation();
	const [message, setMessage] = useState("");
	const [conversationId, setConversationId] = useState<string | null>(
		conversationIdParam,
	);
	const [messages, setMessages] = useState<AIMessage[]>([]);
	const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
		propertyId,
	);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	// Initialize messages from conversation if available
	useEffect(() => {
		if (conversationData?.messages) {
			setMessages(conversationData.messages);
		} else if (conversationIdParam) {
			refetchConversation();
		}
	}, [conversationData, conversationIdParam, refetchConversation]);

	// Auto-scroll to bottom when new messages arrive
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	const handleSendMessage = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!message.trim() || isSending) return;

		const userMessage = message.trim();
		setMessage("");

		// Add user message to UI immediately
		const newUserMessage: AIMessage = {
			role: "user",
			content: userMessage,
			timestamp: new Date().toISOString(),
		};
		setMessages((prev) => [...prev, newUserMessage]);

		try {
			const response = await chat({
				message: userMessage,
				property_id: selectedPropertyId || undefined,
				conversation_id: conversationId || undefined,
			}).unwrap();

			// Update conversation ID if this is a new conversation
			if (response.conversation_id && !conversationId) {
				setConversationId(response.conversation_id);
				// Update URL without reload
				const newParams = new URLSearchParams(searchParams);
				newParams.set("conversation_id", response.conversation_id);
				navigate(`/ai-assistant?${newParams.toString()}`, { replace: true });
			}

			// Add AI response to messages
			const aiMessage: AIMessage = {
				role: "assistant",
				content: response.message,
				timestamp: new Date().toISOString(),
			};
			setMessages((prev) => [...prev, aiMessage]);

			// Refresh conversation if we have a conversation ID
			if (response.conversation_id) {
				refetchConversation();
			}
		} catch (error: any) {
			console.error("Failed to send message:", error);
			// Remove the user message on error
			setMessages((prev) => prev.slice(0, -1));
			alert(
				error?.data?.error ||
					error?.data?.message ||
					"Failed to send message. Please try again.",
			);
		}
	};

	const handleNewConversation = () => {
		setConversationId(null);
		setMessages([]);
		setSelectedPropertyId(null);
		navigate("/ai-assistant");
	};

	const handleSelectConversation = (id: string) => {
		setConversationId(id);
		navigate(`/ai-assistant?conversation_id=${id}`);
	};

	const handleSelectProperty = (id: string) => {
		setSelectedPropertyId(id);
		const newParams = new URLSearchParams(searchParams);
		newParams.set("property_id", id);
		if (conversationId) {
			newParams.set("conversation_id", conversationId);
		}
		navigate(`/ai-assistant?${newParams.toString()}`);
	};

	const selectedProperty = propertiesData?.results?.find(
		(p: Property) => p.id === selectedPropertyId,
	);

	// Determine layout based on user role
	const Layout = user?.role === "admin" ? AdminLayout : UserLayout;

	return (
		<Layout>
			<div className="flex h-[calc(100vh-4rem)] -m-6">
				{/* Left Sidebar - Conversations & Properties */}
				<div className="w-80 flex-shrink-0 border-r border-border bg-card hidden md:block overflow-y-auto">
					<div className="p-4 space-y-4">
						{/* New Conversation Button */}
						<Button
							onClick={handleNewConversation}
							className="w-full"
							iconName="Plus"
							iconPosition="left"
						>
							New Conversation
						</Button>

						{/* Property Selector */}
						<div>
							<h3 className="text-sm font-semibold text-foreground mb-2">
								Select Property (Optional)
							</h3>
							<select
								value={selectedPropertyId || ""}
								onChange={(e) => handleSelectProperty(e.target.value || "")}
								className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
							>
								<option value="">General Questions</option>
								{propertiesData?.results?.map((property: Property) => (
									<option key={property.id} value={property.id}>
										{property.name}
									</option>
								))}
							</select>
						</div>

						{/* Conversations List */}
						<div>
							<h3 className="text-sm font-semibold text-foreground mb-2">
								Recent Conversations
							</h3>
							<div className="space-y-2">
								{!conversationsData || conversationsData.length === 0 ? (
									<p className="text-sm text-muted-foreground">
										No conversations yet
									</p>
								) : (
									conversationsData.map((conv: any) => (
										<button
											key={conv.id}
											onClick={() => handleSelectConversation(conv.id)}
											className={`w-full text-left p-3 rounded-lg border transition-colors ${
												conversationId === conv.id
													? "bg-primary/10 border-primary"
													: "bg-background border-border hover:bg-muted"
											}`}
										>
											<p className="text-sm font-medium text-foreground truncate">
												{conv.property_name || "General Chat"}
											</p>
											{conv.last_message && (
												<p className="text-xs text-muted-foreground mt-1 line-clamp-2">
													{conv.last_message.content}
												</p>
											)}
										</button>
									))
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Main Chat Area */}
				<div className="flex-1 flex flex-col">
					{/* Chat Header */}
					<div className="bg-card border-b border-border px-6 py-4">
						<div className="flex items-center justify-between">
							<div>
								<h1 className="text-xl font-semibold text-foreground">
									AI Property Assistant
								</h1>
								{selectedProperty && (
									<p className="text-sm text-muted-foreground mt-1">
										Discussing: {selectedProperty.name}
									</p>
								)}
							</div>
						</div>
					</div>

					{/* Messages Area */}
					<div className="flex-1 overflow-y-auto p-6 bg-muted/30">
						{messages.length === 0 ? (
							<div className="flex flex-col items-center justify-center h-full text-center">
								<div className="w-20 h-20 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-6">
									<Icon name="Sparkles" size={40} />
								</div>
								<h2 className="text-2xl font-semibold text-foreground mb-3">
									Start a Conversation
								</h2>
								<p className="text-muted-foreground mb-6 max-w-md">
									Ask me anything about properties, get recommendations, or
									compare options. I'm here to help you find your perfect home!
								</p>
								<div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl">
									<button
										onClick={() =>
											setMessage("What properties do you recommend?")
										}
										className="p-4 bg-card border border-border rounded-lg hover:bg-muted transition-colors text-left"
									>
										<Icon
											name="Lightbulb"
											size={20}
											className="text-accent mb-2"
										/>
										<p className="text-sm font-medium text-foreground">
											Get Recommendations
										</p>
									</button>
									<button
										onClick={() => setMessage("Help me compare properties")}
										className="p-4 bg-card border border-border rounded-lg hover:bg-muted transition-colors text-left"
									>
										<Icon
											name="BarChart"
											size={20}
											className="text-primary mb-2"
										/>
										<p className="text-sm font-medium text-foreground">
											Compare Properties
										</p>
									</button>
									<button
										onClick={() =>
											setMessage("What should I know about this property?")
										}
										className="p-4 bg-card border border-border rounded-lg hover:bg-muted transition-colors text-left"
									>
										<Icon
											name="MessageSquare"
											size={20}
											className="text-success mb-2"
										/>
										<p className="text-sm font-medium text-foreground">
											Ask Questions
										</p>
									</button>
								</div>
							</div>
						) : (
							<div className="space-y-4 max-w-4xl mx-auto">
								{messages.map((msg, index) => (
									<div
										key={index}
										className={`flex ${
											msg.role === "user" ? "justify-end" : "justify-start"
										}`}
									>
										<div
											className={`max-w-[80%] rounded-lg p-4 ${
												msg.role === "user"
													? "bg-primary text-primary-foreground"
													: "bg-card border border-border text-foreground"
											}`}
										>
											<p className="whitespace-pre-wrap break-words">
												{msg.content}
											</p>
											<p
												className={`text-xs mt-2 ${
													msg.role === "user"
														? "text-primary-foreground/70"
														: "text-muted-foreground"
												}`}
											>
												{new Date(msg.timestamp).toLocaleTimeString()}
											</p>
										</div>
									</div>
								))}
								{isSending && (
									<div className="flex justify-start">
										<div className="bg-card border border-border rounded-lg p-4">
											<div className="flex items-center space-x-2">
												<Icon
													name="Loader2"
													size={16}
													className="animate-spin text-primary"
												/>
												<span className="text-sm text-muted-foreground">
													AI is thinking...
												</span>
											</div>
										</div>
									</div>
								)}
								<div ref={messagesEndRef} />
							</div>
						)}
					</div>

					{/* Message Input */}
					<form
						onSubmit={handleSendMessage}
						className="border-t border-border bg-card p-4"
					>
						<div className="flex items-end gap-3 max-w-4xl mx-auto">
							<div className="flex-1">
								<Input
									value={message}
									onChange={(e) => setMessage(e.target.value)}
									placeholder="Ask about properties, get recommendations, or compare options..."
									disabled={isSending}
									className="w-full"
								/>
							</div>
							<Button
								type="submit"
								disabled={!message.trim() || isSending}
								iconName="Send"
								iconPosition="left"
							>
								{isSending ? "Sending..." : "Send"}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</Layout>
	);
};

// Wrap with ProtectedRoute
const AIAssistantPage: React.FC = () => {
	return (
		<ProtectedRoute allowedRoles={["admin", "tenant", "technician", "agent"]}>
			<AIAssistant />
		</ProtectedRoute>
	);
};

export default AIAssistantPage;
